import React, { useRef } from 'react';
import { displayPrimaryPlantName } from '../utils/plantDisplayName';

function emojiFor(plantType) {
  switch ((plantType || '').toLowerCase()) {
    case 'tree': return '🌳';
    case 'shrub': return '🪴';
    case 'herb': return '🌿';
    case 'vegetable': return '🥬';
    case 'lawn': return '🌱';
    case 'annual': return '🌸';
    case 'perennial': return '🌼';
    default: return '🌿';
  }
}

// Renders a draggable circular marker absolutely positioned inside its parent (.relative).
// Position is x%/y% in the parent's coordinate system.
//
// Gestures:
//  - tap / left-click                -> onTap(marker)    (opens plant detail)
//  - long-press (touch, ~450 ms)     -> onLongPressStart (starts drag-to-reposition)
//  - right-click (mouse contextmenu) -> onOptionsMenu    (opens Reposition/Remove sheet)
// Touch-initiated contextmenu events (Android long-press) are suppressed so
// mobile users get drag, not a menu.
export default function PlantMarker({
  marker,
  onTap,
  onLongPressStart,
  onOptionsMenu,
  isActive = false,
  isDragging = false,
}) {
  const label = marker.label || marker.nickname || displayPrimaryPlantName(marker);
  const x = Number(marker.x_percent);
  const y = Number(marker.y_percent);
  const thumb = marker.thumbnail_url || null;

  const pressTimerRef = useRef(null);
  const lastPointerTypeRef = useRef('mouse');

  const startPress = (e) => {
    lastPointerTypeRef.current = e.pointerType || 'mouse';
    pressTimerRef.current = setTimeout(() => {
      onLongPressStart?.(e, marker);
    }, 450);
  };
  const clearPress = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };

  const handleContextMenu = (e) => {
    // Always suppress the browser's default context menu on the marker.
    e.preventDefault();
    e.stopPropagation();
    // Only treat it as an "open options" gesture when it came from a mouse.
    // On touch devices, long-press must remain drag-to-reposition.
    if (lastPointerTypeRef.current !== 'touch') {
      onOptionsMenu?.(marker);
    }
  };

  return (
    <div
      className="absolute z-10 -translate-x-1/2 -translate-y-1/2 select-none"
      style={{ left: `${x}%`, top: `${y}%` }}
      onClick={(e) => { e.stopPropagation(); onTap?.(marker); }}
      onPointerDown={startPress}
      onPointerUp={clearPress}
      onPointerLeave={clearPress}
      onPointerCancel={clearPress}
      onContextMenu={handleContextMenu}
    >
      <div className={`flex flex-col items-center ${isDragging ? 'scale-110' : ''}`}>
        <div
          className={
            `flex items-center justify-center rounded-full shadow-md overflow-hidden ` +
            `h-11 w-11 text-xl ` +
            (isActive
              ? 'bg-garden-primary text-white ring-4 ring-garden-primary/30'
              : 'bg-white ring-2 ring-garden-primary/60')
          }
        >
          {thumb ? (
            <img
              src={thumb}
              alt=""
              className="w-full h-full object-cover"
              draggable={false}
            />
          ) : (
            <span aria-hidden>{emojiFor(marker.plant_type)}</span>
          )}
        </div>
        <div className="mt-1 px-2 py-0.5 bg-white/95 text-xs font-medium text-garden-ink rounded-full shadow max-w-[120px] truncate">
          {label}
        </div>
      </div>
    </div>
  );
}
